import React, { FC } from 'react'

import { ThemeProps } from '@hhgtech/hhg-components'
import { NavigatorLayout } from 'components'
import { Container } from 'components/atoms/container'
import { SectionContainerStyled } from 'components/atoms/container/index.styled'
import {
  IExpertDetailPage,
  IExpertInfo,
  IExpertResume,
  IResumeGroup,
  IContactExpertInfo,
} from 'interfaces/categories.interface'
import { LOCALE_SPECS } from 'interfaces/constants'
import { useRouter } from 'next/router'
import DotIcon from 'public/svg/categories/dot.svg'
import ExperienceDetailIcon from 'public/svg/categories/experience-detail.svg'
import ProfessionIcon from 'public/svg/categories/profession.svg'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { deviceLayout } from 'styles/breakpoint'

import Breadcrumb from '../Breadcrumb'
import ExpertInfo from './ExpertInfo'
import ExpertResume from './ExpertResume'

const WrapperLayout = styled.div`
  .root-page,
  .topbar {
    background: ${(props: ThemeProps) => props.theme.mbColors.lightBlue};
  }
  .root-container {
    background-color: transparent;
    font-size: 14px;
    line-height: 24px;
  }
  .footer-container {
    background: ${(props: ThemeProps) => props.theme.colors.white};
  }

  .expert-detail-container {
    ${deviceLayout.spLayout`
      background-color: ${(props: ThemeProps) =>
        props.theme.mbColors.lightBlue};
    `}

    .articles-container {
      padding: 24px 0;
    }

    .avatar-container {
      ${deviceLayout.spLayout`
       background-color: white;
       margin-top: 80px;
   `}
      border-radius: 100% 100% 0 0 / 30% 30%;
      .avatar-icon,
      .expert-info {
        top: -36px;
        ${deviceLayout.pcLayout`
        top: 50px;
    `}
      }
    }

    .general {
      /* + 50px at margin-top above */
      ${deviceLayout.pcLayout`
        padding: 74px 0;
    `}
      padding: 24px 0;
      background: ${(props: ThemeProps) => props.theme.colors.white};
    }
    .header-breadcrumb {
      position: relative;
      background: ${(props: ThemeProps) => props.theme.mbColors.lightBlue};
      ${deviceLayout.pcLayout`
        &::before {
            height: 150px;
            width: 100%;
            content: '';
            position: absolute;
            transform: rotate(180deg);
            border-radius: 2150px 2150px 0 0 / 100px 100px 0 0;
            background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
              props['data-bg'] || props.theme.mbColors.lightBlue};
            }
   `}
    }
  }
`

const ExpertDetailPage: FC<IExpertDetailPage> = (props: IExpertDetailPage) => {
  const { expert, footerMenu, dataNavMenuContent, isMobile, side } = props
  const { formatMessage: f } = useIntl()
  const { locale = 'vi-VN' } = useRouter()

  const breadcrumbs = {
    secondItem: {
      label: f({ id: 'category.expert.heading' }, { CatName: '' }),
      value: '/expert',
      active: true,
    },
    breadcrumbItems: [
      {
        label: expert.displayName,
        value: expert.profileUrl,
        dataEventCategory: 'Breadcrumb',
        dataEventAction: 'Click',
        active: true,
      },
    ],
  }

  const breadcrumbsAuthor = {
    secondItem: {
      label: expert.displayName,
      value: expert.profileUrl,
      dataEventCategory: 'Breadcrumb',
      dataEventAction: 'Click',
      active: true,
    },
  }

  const breadcrumbsProps = side === 'author' ? breadcrumbsAuthor : breadcrumbs

  const expertInfo: IExpertInfo = {
    isMobile,
    role: expert.role,
    urlImg: expert.avatar,
    name: expert.displayName,
    major: expert.specialty || '',
    organisationUrl: expert.organisation_url || expert.profileUrl || '/',
    contact: expert.contact as IContactExpertInfo,
  }

  const resumeGroups: IResumeGroup[] = [
    {
      icon: <ProfessionIcon />,
      label: f({ id: 'expert.title.degree' }),
      details:
        expert?.educations?.map((education) => ({
          icon: <DotIcon />,
          label: `${education.schoolName} - ${education.study}`,
        })) || [],
    },
    {
      icon: <ExperienceDetailIcon />,
      label: f({ id: 'expert.title.medicalExperience' }),
      details:
        expert?.experiences?.map((experience) => ({
          icon: <DotIcon />,
          label: (
            <>
              <p className="period">{experience.duration}</p>
              <p>{`${experience.organization} - ${experience.title}`}</p>
            </>
          ),
        })) || [],
    },
  ]

  const expertResume: IExpertResume = {
    resumeGroups,
    about: expert.description,
    articles: expert.relatedPost,
  }

  return (
    <WrapperLayout>
      <NavigatorLayout
        SEOData={{
          apiMetaData: expert.metaTag,
          isIndexable: true,
          hasNoAmp: true,
        }}
        type="profile"
        image={expertInfo.urlImg || LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
        footerMenu={footerMenu}
        dataNavMenuContent={dataNavMenuContent}
      >
        <SectionContainerStyled className="expert-detail-container">
          <SectionContainerStyled className="header-breadcrumb">
            <Container className="breadcrumb">
              <Breadcrumb {...breadcrumbsProps} />
            </Container>
          </SectionContainerStyled>
          <SectionContainerStyled className="avatar-container">
            <ExpertInfo {...expertInfo} />
          </SectionContainerStyled>
          <SectionContainerStyled className="general">
            <ExpertResume {...expertResume} />
          </SectionContainerStyled>
        </SectionContainerStyled>
      </NavigatorLayout>
    </WrapperLayout>
  )
}

export default ExpertDetailPage
